/**
 * QuickTagControl (Plan 04-07 / FIN-04).
 *
 * Three buttons shortlist / maybe / no → POST shortlist-tag immediately (independent of
 * rubric save). The active button reflects the current tag; pressing one optimistically
 * sets local state and persists. Active state contrasts with inactive + page bg (BUG-021).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { mapErrorSlug } from "@/lib/error-slug-mapper";

import type { ShortlistTag } from "./api";
import { useShortlistTagMutation } from "./hooks";

const TAGS: ShortlistTag[] = ["shortlist", "maybe", "no"];

export interface QuickTagControlProps {
  candidateId: number;
  currentTag: ShortlistTag | null;
}

export function QuickTagControl(props: QuickTagControlProps) {
  const { t } = useTranslation("rubric");
  const [active, setActive] = useState<ShortlistTag | null>(props.currentTag);
  const tagMutation = useShortlistTagMutation(props.candidateId);

  async function pick(tag: ShortlistTag) {
    const prev = active;
    setActive(tag); // optimistic
    try {
      await tagMutation.mutateAsync(tag);
      toast.success(t("quicktag.saved"));
    } catch (err) {
      setActive(prev);
      toast.error(mapErrorSlug(err));
    }
  }

  return (
    <section className="space-y-2" data-testid={`quick-tag-${props.candidateId}`}>
      <h3 className="text-sm font-semibold">{t("quicktag.title")}</h3>
      <div className="flex gap-2">
        {TAGS.map((tag) => {
          const isActive = active === tag;
          return (
            <button
              key={tag}
              type="button"
              onClick={() => pick(tag)}
              disabled={tagMutation.isPending}
              aria-pressed={isActive}
              data-testid={`quick-tag-btn-${tag}`}
              className={`min-h-[44px] rounded-md border px-3 text-sm font-medium ${
                isActive
                  ? "border-primary bg-primary text-primary-foreground shadow-sm"
                  : "border-input bg-background text-foreground hover:bg-accent"
              }`}
            >
              {t(`quicktag.${tag}`)}
            </button>
          );
        })}
      </div>
    </section>
  );
}
